import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Form, Input, Button, Typography, Card, Select, message, Alert, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, InfoCircleOutlined, SendOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import LandingLayout from '../components/Layout/LandingLayout';

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface FormData {
  email: string;
  password: string;
  remember?: boolean;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const onFinish = async (values: FormData) => {
    setLoading(true);
    try {
      await login(values.email, values.password);
      message.success(t('login.success'));
      navigate('/app/dashboard');
    } catch (error: any) {
      // Check if the error message is about inactive user
      const errorMessage = error?.message || '';
      if (errorMessage.includes('Подайте заявку на ранний доступ') || 
          errorMessage.includes('QAappbot') || 
          errorMessage.includes('дождитесь обработки')) {
        message.error(t('login.inactiveUser'));
      } else {
        message.error(t('login.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const formCardStyle = {
    width: '100%',
    padding: '2px',
    background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(26, 86, 219, 0.15)',
    border: 'none',
    overflow: 'hidden'
  };

  const innerFormStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '40px 20px',
    position: 'relative' as const,
    overflow: 'hidden'
  };

  const fieldStyle = (fieldName: string) => ({
    height: '52px',
    fontSize: '16px',
    borderRadius: '12px',
    border: focusedField === fieldName ? '2px solid #1a56db' : '2px solid #e8e8e8',
    boxShadow: focusedField === fieldName ? '0 0 0 3px rgba(26, 86, 219, 0.1)' : 'none',
    transition: 'all 0.3s ease',
    background: focusedField === fieldName ? '#f6f9ff' : 'white'
  });

  const buttonStyle = {
    height: '52px',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
    border: 'none',
    boxShadow: '0 8px 16px rgba(26, 86, 219, 0.3)',
    transition: 'all 0.3s ease',
    position: 'relative' as const,
    overflow: 'hidden'
  };

  return (
    <LandingLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '24px', 
          width: '100%' 
        }}>
          {/* Access Information Card */}
          <Card style={{ width: '100%' }}>
            <Alert
              message={<Title level={3} style={{ margin: 0, color: '#1890ff' }}>{t('login.accessInfo.title')}</Title>}
              description={
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px', 
                  width: '100%' 
                }}>
                  <Paragraph style={{ margin: 0, fontSize: '16px', lineHeight: '1.6' }}>
                    {t('login.accessInfo.description')}
                  </Paragraph>
                  <Paragraph style={{ margin: 0, fontSize: '16px', lineHeight: '1.6' }}>
                    {t('login.accessInfo.process')}
                  </Paragraph>
                  <Paragraph style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                    {t('login.accessInfo.telegram')}
                  </Paragraph>
                  <Paragraph style={{ margin: '12px 0 0 0', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    {t('login.accessInfo.note')}
                  </Paragraph>
                  <div style={{ marginTop: '16px' }}>
                    <a 
                      href="https://t.me/QAappbot" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button 
                        type="primary" 
                        icon={<SendOutlined />} 
                        size="large"
                        style={{ 
                          background: '#1890ff',
                          borderColor: '#1890ff',
                          fontWeight: 'bold'
                        }}
                      >
                        @QAappbot
                      </Button>
                    </a>
                  </div>
                </div>
              }
              type="info"
              icon={<InfoCircleOutlined style={{ fontSize: '24px' }} />}
              showIcon
              style={{
                padding: '24px',
                border: '2px solid #1890ff',
                borderRadius: '12px',
                background: '#f6f9ff'
              }}
            />
          </Card>

          {/* Login Form Card */}
          <Card style={formCardStyle}>
            <div style={innerFormStyle}>
              {/* Декоративные элементы */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '100px',
                height: '100px',
                background: 'linear-gradient(135deg, rgba(26, 86, 219, 0.1) 0%, rgba(24, 144, 255, 0.1) 100%)',
                borderRadius: '50%',
                zIndex: 0
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, rgba(26, 86, 219, 0.05) 0%, rgba(24, 144, 255, 0.05) 100%)',
                borderRadius: '50%',
                zIndex: 0
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <Title level={2} style={{ 
                  textAlign: 'center', 
                  marginBottom: 40, 
                  fontSize: '32px',
                  background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold'
                }}>
                  {t('login.title')}
                </Title>
                
                <div style={{ width: '100%', margin: '0 auto' }}>
                  <Form
                    name="login"
                    initialValues={{ remember: true }}
                    onFinish={onFinish}
                    layout="vertical"
                    size="large"
                    style={{ width: '100%' }}
                  >
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: t('login.emailRequired') },
                        { type: 'email', message: t('login.emailInvalid') }
                      ]}
                      style={{ marginBottom: '24px' }}
                    >
                      <Input
                        prefix={<UserOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                        placeholder={t('login.email')}
                        size="large"
                        style={fieldStyle('email')}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      rules={[{ required: true, message: t('login.passwordRequired') }]}
                      style={{ marginBottom: '24px' }}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                        placeholder={t('login.password')}
                        size="large"
                        style={fieldStyle('password')}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#1a56db" /> : <EyeInvisibleOutlined style={{ color: '#1a56db' }} />)}
                      />
                    </Form.Item>

                    <Form.Item
                      name="remember"
                      valuePropName="checked"
                      style={{ marginBottom: '32px' }}
                    >
                      <Checkbox style={{ fontSize: '14px', color: '#666' }}>
                        Запомнить меня
                      </Checkbox>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: '24px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        block 
                        size="large"
                        loading={loading}
                        style={buttonStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 12px 24px rgba(26, 86, 219, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(26, 86, 219, 0.3)';
                        }}
                      >
                        {loading ? 'Входим...' : t('login.submit')}
                      </Button>
                    </Form.Item>

                    <div style={{ 
                      textAlign: 'center', 
                      fontSize: '16px',
                      padding: '16px 0',
                      borderTop: '1px solid #e8e8e8',
                      marginTop: '16px'
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ color: '#666' }}>{t('login.noAccount')} </span>
                        <Link 
                          to="/register"
                          style={{ 
                            fontSize: '16px',
                            fontWeight: 'bold',
                            background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textDecoration: 'none'
                          }}
                        >
                          {t('login.register')}
                        </Link>
                      </div>
                      <div>
                        <Link 
                          to="/forgot-password"
                          style={{ 
                            fontSize: '14px',
                            color: '#1890ff',
                            textDecoration: 'none'
                          }}
                        >
                          {t('login.forgotPassword')}
                        </Link>
                      </div>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </LandingLayout>
  );
};

export default LoginPage; 